/**
 * The served analysis, and starting the run that produces it.
 *
 * One query for the whole tab: the counts, the verdicts and every document's
 * findings come in one piece, so the screen never draws a verdict from one
 * read beside a headline from another. The run's motion rides the task's
 * stream and is patched into this query by ``useFileProcessing``.
 */

import { useEffect, useState } from "react";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AdmissionRuling,
  AnalysisResponse,
} from "@patent-studio/api-types";
import { readAnalysis, ruleAdmission, startRun } from "@/services/novelty";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";
import { useTask } from "@/hooks/use-tasks";
import { priorArtOf } from "@/v2/novelty/prior-art-list";
import {
  noveltyAnalysisKey,
  positionsKey,
  thesisGateScopes,
} from "./query-keys";

export function useNoveltyAnalysis(taskId: string) {
  return useQuery({
    queryKey: noveltyAnalysisKey(taskId),
    queryFn: () => readAnalysis(taskId),
  });
}

/**
 * Read the analysis again: in place while a screen shows it, and otherwise by
 * dropping the cached read, so the next screen to show it reads it fresh and
 * never draws the old one first. Each read carries every finding, so nothing
 * is read for a screen that is not there.
 */
export function rereadAnalysis(qc: QueryClient, taskId: string): void {
  const queryKey = noveltyAnalysisKey(taskId);
  if (qc.getQueryCache().find({ queryKey, exact: true })?.isActive()) {
    void qc.invalidateQueries({ queryKey });
  } else {
    qc.removeQueries({ queryKey, exact: true });
  }
}

/**
 * Read the analysis again whenever the prior art library moves: a document
 * added, read, failed, typed or removed. Whether the analysis is out of date
 * turns on the library, which every write to it patches into the cached task
 * (upload, type, delete, replace, the Intake slots and the worker's stream),
 * so this one watch covers them all. Documents are compared by the read status
 * the server serves, so the steps a document takes while it is read ask nothing. The open draft holds this watch, so a change made on
 * any page reaches the analysis before the attorney opens it. Each draft keeps
 * the library seen for it, so opening another draft reads nothing again and a
 * change written to a draft while another is open is caught on return.
 */
export function useAnalysisFollowsTheLibrary(taskId: string | undefined) {
  const qc = useQueryClient();
  const { data: task } = useTask(taskId ?? null);
  const library =
    task &&
    priorArtOf(task.files)
      .map((f) => `${f.file_id}:${f.read_status}:${f.literature_type ?? ""}`)
      .join("|");
  const [seen] = useState(() => new Map<string, string>());
  useEffect(() => {
    if (library === undefined || taskId === undefined) return;
    const before = seen.get(taskId);
    if (before !== undefined && before !== library) rereadAnalysis(qc, taskId);
    seen.set(taskId, library);
  }, [library, qc, seen, taskId]);
}

/**
 * Start a run: every position, or the ones named. Where the analysis is
 * already in the cache, the accepted state is patched into it so the tab
 * reads as running before the stream's first event. The gate is read again
 * either way, since a refusal names the conditions the gate lists and an
 * acceptance moves the run's state on it.
 */
export function useStartRun(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.noveltyRunStart,
    mutationFn: (positionIds?: string[]) => startRun(taskId, positionIds),
    onSuccess: async (accepted) => {
      const key = noveltyAnalysisKey(taskId);
      // A read in flight would land after this patch and put the pre-start
      // status back until the stream's first event; it is stopped first.
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<AnalysisResponse>(key, (prev) =>
        prev
          ? {
              ...prev,
              status: accepted.status,
              run_id: accepted.run_id,
              error: null,
            }
          : prev,
      );
    },
    onSettled: () =>
      Promise.all(
        thesisGateScopes(taskId).map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

/**
 * Accept or strike one entry of what the disclosure admits. The analysis the
 * server answers is put in the cache as it is, since striking an entry moves
 * the verdicts and strips it read; the gates and the positions are read again,
 * since the thesis address moves with them and a position's confirmation is
 * read from its strip.
 */
export function useRuleAdmission(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.noveltyAdmissionRule,
    mutationFn: ({
      entryId,
      ruling,
    }: {
      entryId: string;
      ruling: AdmissionRuling;
    }) => ruleAdmission(taskId, entryId, ruling),
    onSuccess: async (analysis) => {
      const key = noveltyAnalysisKey(taskId);
      // A read in flight was sent before the ruling and would land after it
      // with the entry as it stood; it is stopped first.
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<AnalysisResponse>(key, analysis);
    },
    onSettled: () =>
      Promise.all(
        [...thesisGateScopes(taskId), positionsKey(taskId)].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}
